import React, { useEffect, useState } from 'react';
import { FormCheckbox, FormGroup, Heading, InPageAlert, Input, MoreInfoModal } from '@snsw/react-component-library';
import { ContactMethod } from '../common/ContactMethod';
import PropTypes from 'prop-types';
import {
  isEmpty,
  isValidEmail,
  validateExt,
  validateFormattedAddress,
  validateMandatoryAndMaxLength,
  validateOptionalAndMaxLength,
} from 'shared/utils/Validation';
import { OrganisationSearchAutoComplete } from 'shared/components/organisationSearch/OrganisationSearchAutoComplete';
import { LocalAndInternationalAddress } from 'shared/components/address/LocalAndInternationalAddress';
import styles from '../PlantDesignRegistrationHolder.module.scss';
import { Box, FlexBox } from '@onegov/react-component-library';
import { PlantDesignRegistrationHolderPage } from 'plantDesign/constants/plantDesignRegistrationHolderPage';
import { isRegisteredInAustraliaOutsideNSW } from '../common/utils';

const OrganisationHolderFields = React.forwardRef((props, ref) => {
  const { value: storedHolderDetails = {}, onErrorChange = () => {}, onChange = () => {} } = props;
  const {
    emailAddress: storedEmailAddress = '',
    organisation: {
      abn = '',
      organisationName = '',
      selectedBusinessName: storedSelectedBusinessName = '',
      acn = '',
    } = {},
    businessAddress: storedBusinessAddress = {},
    isSameAddress: storedIsSameAddress = true,
    mailingAddress: storedMailingAddress = {},
    reasonToRegister: storedReasonToRegister = '',
  } = storedHolderDetails;

  const [emailAddress, setEmailAddress] = useState(storedEmailAddress);

  const [organisation, setOrganisation] = useState({
    abn,
    acn,
    name: organisationName,
  });
  const [selectedBusinessName, setSelectedBusinessName] = useState(storedSelectedBusinessName);
  const [contactMethod, setContactMethod] = useState(storedHolderDetails?.contactMethod);
  const [businessAddress, setBusinessAddress] = useState(storedBusinessAddress);
  const [isSameAddress, setIsSameAddress] = useState(storedIsSameAddress);
  const [mailingAddress, setMailingAddress] = useState(storedMailingAddress);
  const [reasonToRegister, setReasonToRegister] = useState(storedReasonToRegister);

  const [errors, setErrors] = useState({});
  const [showErrors, setShowErrors] = useState(false);

  const [errorSummary, setErrorSummary] = useState([]);
  const [contactErrorSummary, setContactErrorSummary] = useState([]);
  const [businessAddrErrorSummary, setBusinessAddrErrorSummary] = useState([]);
  const [mailingAddrErrorSummary, setMailingAddrErrorSummary] = useState([]);

  useEffect(() => {
    if (props.onChange) {
      let mydetails = {
        organisation: {
          abn: organisation?.abn,
          organisationName: organisation?.name,
          selectedBusinessName,
          ...(!isEmpty(organisation?.acn) &&
            organisation?.entityType === 'Other Incorporated Entity' && {
              arbn: organisation?.acn,
            }),
          ...(!isEmpty(organisation?.acn) &&
            organisation?.entityType !== 'Other Incorporated Entity' && {
              acn: organisation?.acn,
            }),
        },
        emailAddress,
        contactMethod,
        businessAddress,
        isSameAddress,
        ...(!isSameAddress && {
          mailingAddress,
        }),
        ...(isRegisteredInAustraliaOutsideNSW(businessAddress) && {
          reasonToRegister,
        }),
      };

      onChange(mydetails);
    }

    if (showErrors) validateExt(validators, setErrors, setErrorSummary);
  }, [
    organisation,
    selectedBusinessName,
    businessAddress,
    emailAddress,
    contactMethod,
    reasonToRegister,
    isSameAddress,
    mailingAddress,
  ]);

  useEffect(() => {
    onErrorChange(errorSummary.concat(contactErrorSummary, businessAddrErrorSummary, mailingAddrErrorSummary));
  }, [errorSummary, contactErrorSummary, businessAddrErrorSummary, mailingAddrErrorSummary]);

  const validators = [
    {
      fieldName: 'organisation',
      fieldValue: organisation,
      isValid: (v) => !isEmpty(v?.abn) || !isEmpty(v?.acn),
      errorMessage: () => 'Enter a valid ABN, ACN or ARBN',
    },

    {
      fieldName: 'emailAddress',
      fieldValue: emailAddress,
      isValid: (v) => (isEmpty(organisation.abn) && isEmpty(organisation.acn)) || isValidEmail(v),
      errorMessage: (v) => (isEmpty(v) ? 'Please enter details' : 'Please enter valid email address'),
    },
    {
      fieldName: 'reasonToRegister',
      fieldValue: reasonToRegister,
      isValid: (v) => !isEmpty(v) || !isRegisteredInAustraliaOutsideNSW(businessAddress),
      errorMessage: () => 'Please enter details',
    },
  ];

  const validateStreetAddress = (street, isPoboxAllowed = false) => {
    const result = validateMandatoryAndMaxLength(street, 64);
    if (!result.status) return result;

    if (street.toLowerCase().startsWith('po box') && !isPoboxAllowed)
      return { status: false, errorMessage: 'PO Box address is not allowed' };

    return { status: true, errorMessage: '' };
  };

  const internationalAddressValidator = {
    validateCountry: (country) => validateMandatoryAndMaxLength(country, 40),
    validateStreet: validateStreetAddress,
    validateCity: (city) => validateOptionalAndMaxLength(city, 40),
    validateProvince: (province) => validateOptionalAndMaxLength(province, 40),
    validateZipCode: (zipCode) => validateOptionalAndMaxLength(zipCode, 12),
  };

  const localAddressValidator = {
    validateStreet: (street) => validateStreetAddress(street),
    validateSuburb: (suburb) => validateMandatoryAndMaxLength(suburb, 40),
    validateState: (state) => validateMandatoryAndMaxLength(state, 3),
    validatePostCode: (postCode) => validateMandatoryAndMaxLength(postCode, 4),
    validateFormattedAddress: (formattedAddress) => validateFormattedAddress(formattedAddress),
  };

  const localMailingAddressValidator = {
    ...localAddressValidator,
    validateStreet: (street) => validateStreetAddress(street, true),
    validateFormattedAddress: (formattedAddress) => validateFormattedAddress(formattedAddress, true),
  };

  const internationalMailingAddressValidator = {
    ...internationalAddressValidator,
    validateStreet: (street) => validateStreetAddress(street, true),
  };

  const contactMethodRef = React.useRef();
  const businessAddRef = React.useRef();
  const mailingAddRef = React.useRef();

  if (ref) {
    ref.current = {
      validate: () => {
        const isCurrentComponentValid = validateExt(validators, setErrors, setErrorSummary);
        let contactMethodValid = true;
        let businessAddressValid = true;

        if (contactMethodRef.current) contactMethodValid = contactMethodRef.current.validate();

        if (businessAddRef.current) businessAddressValid = businessAddRef.current.validate();

        let mailingAddressValid = true;

        if (mailingAddRef.current && !isSameAddress) mailingAddressValid = mailingAddRef.current.validate();

        setShowErrors(!isCurrentComponentValid);

        return isCurrentComponentValid && contactMethodValid && businessAddressValid && mailingAddressValid;
      },
    };
  }

  return (
    <>
      <InPageAlert variant="info" title="Overseas organisations" className={styles.marginTopThreeRem}>
        <p>You can apply as an organisation if you have:</p>
        <ul className={styles.listItem}>
          <li>
            an{' '}
            <a
              href="https://www.ppsr.gov.au/glossary/arbn-australian-registered-body-number"
              target="_blank"
              rel="noreferrer"
            >
              ARBN
            </a>{' '}
            or{' '}
            <a href="https://www.ppsr.gov.au/glossary/acn-australian-company-number" target="_blank" rel="noreferrer">
              ACN
            </a>
            , or
          </li>
          <li>nominate an Australian business to apply on your behalf.</li>
        </ul>
        <p>If you live overseas, you can also apply as an individual.</p>
      </InPageAlert>
      <Heading level={2}>Organisation details</Heading>
      {isEmpty(organisation?.name) && (
        <>
          <span className={styles.labelMsg}>Find an organisation</span>
          <FlexBox gap={1} className={styles.marginTopOne}>
            <Box flex="1">
              <span className={styles.helpMsg}>Start typing to search for and ABN, ACN or an ARBN</span>
            </Box>
            <Box flex="1">
              <MoreInfoModal
                helpText={
                  <ul>
                    <li>
                      ABN or Australian Business Number is an 11-digit number that all businesses need to operate.
                    </li>
                    <li>ACN or Australian Company Number is a 9-digit number issued to registered companies only.</li>
                    <li>
                      ARBN or Australian Registered Body Number is a 9-digit number issued to registrable Australian
                      bodies and foreign companies.
                    </li>
                  </ul>
                }
                title="What is the difference between ABN, ACN and ARBN?"
              />
            </Box>
          </FlexBox>
        </>
      )}

      <OrganisationSearchAutoComplete
        id="organisation"
        baseURL={`${process.env.REACT_APP_BASE_URL}${process.env.REACT_APP_URL_PREFIX}`}
        selectedOrganisation={organisation}
        onChangeOrganisation={setOrganisation}
        selectedBusinessName={selectedBusinessName}
        onChangeBusinessName={setSelectedBusinessName}
        hasError={showErrors && errors?.organisation?.invalid}
        errorMessage={errors?.organisation?.errorMessage}
        onClear={() => {
          setOrganisation(undefined);
          setSelectedBusinessName('');
        }}
      />

      {!isEmpty(organisation?.name) && (
        <>
          <FormGroup
            id="emailAddress"
            label={PlantDesignRegistrationHolderPage.organisationEmailAddressLabel}
            errorMessage={errors?.emailAddress?.errorMessage}
            hasError={showErrors && errors?.emailAddress?.invalid}
            margin={{ top: 'sm' }}
          >
            <Input value={emailAddress} onChange={(e) => setEmailAddress(e.target.value)} inputWidth="xl" />
          </FormGroup>

          <ContactMethod
            id="contactMethod"
            ref={contactMethodRef}
            onChange={setContactMethod}
            value={contactMethod}
            onErrorChange={setContactErrorSummary}
          />

          <LocalAndInternationalAddress
            id="businessAddress"
            margin={{ top: 'xl' }}
            baseUrl={`${process.env.REACT_APP_BASE_URL}${process.env.REACT_APP_URL_PREFIX}/addressApi/plant-design`}
            legend="Registered business address"
            onChange={setBusinessAddress}
            value={businessAddress}
            ref={businessAddRef}
            onErrorChange={setBusinessAddrErrorSummary}
            internationalAddressValidator={internationalAddressValidator}
            localAddressValidator={localAddressValidator}
            helpMessage="This cannot be a PO Box"
          />

          {isRegisteredInAustraliaOutsideNSW(businessAddress) && (
            <>
              <InPageAlert
                className={styles.marginTop2rem}
                variant="warning"
                title="We recommend registering this design with your local state regulator."
              >
                <p>
                  You can register this design in NSW, but you will not be eligible for a refund if your application is
                  declined.
                </p>
              </InPageAlert>
              <FormGroup
                id="reasonToRegister"
                label={PlantDesignRegistrationHolderPage.reasonToRegisterInNSWLabel}
                errorMessage={errors?.reasonToRegister?.errorMessage}
                hasError={showErrors && errors?.reasonToRegister?.invalid}
                margin={{ top: 'md' }}
              >
                <Input value={reasonToRegister} onChange={(e) => setReasonToRegister(e.target.value)} />
              </FormGroup>
            </>
          )}

          {businessAddress?.addressType && (
            <>
              <span className={styles.labelMsg + ' ' + styles.marginTopOneHalf}>Mailing address</span>
              <FormCheckbox
                id={`addSecondary`}
                name={`addSecondary`}
                label={PlantDesignRegistrationHolderPage.isMailingAddressSameAsBusinessAddressLabel}
                onChange={() => {
                  setIsSameAddress(!isSameAddress);
                  setMailingAddress({});
                  setMailingAddrErrorSummary([]);
                }}
                checked={isSameAddress}
                value="mailingAddress"
                margin={{ top: 'sm' }}
              />
              {!isSameAddress && (
                <LocalAndInternationalAddress
                  id="mailing"
                  margin={{ top: 'sm' }}
                  baseUrl={`${process.env.REACT_APP_BASE_URL}${process.env.REACT_APP_URL_PREFIX}/addressApi/plant-design`}
                  legend=" "
                  onChange={setMailingAddress}
                  value={mailingAddress}
                  ref={mailingAddRef}
                  onErrorChange={setMailingAddrErrorSummary}
                  internationalAddressValidator={internationalMailingAddressValidator}
                  localAddressValidator={localMailingAddressValidator}
                  helpMessage=" "
                />
              )}
            </>
          )}
        </>
      )}
    </>
  );
});
OrganisationHolderFields.displayName = 'OrganisationHolderFields';

OrganisationHolderFields.propTypes = {
  onChange: PropTypes.func,
  value: PropTypes.object,
};

OrganisationHolderFields.defaultProps = {
  onChange: null,
  value: null,
};

export { OrganisationHolderFields };
